import { EntityStatus, UtcDateString } from '../common';
import { IApiUserAccount } from '../tap-api/user-service.types'
import { MaterialPlanMode } from './raw-data.types';


export type IApiPlanListCounts = {
    total?: number,
    all?: number,
    deleted?: number,
    favorite?: number,
}

export type IApiGetPlanListParams = {
    status?: string,
}

export enum ApsPlanStatusCode {
    New = 'new',
    Reopen = 'reopen',
    Work = 'work',
    Completed = 'completed',
    Deleted = 'deleted',
}

export const ApsPlanStatusSpec: { [key in ApsPlanStatusCode]: { name: string } } = {
    [ApsPlanStatusCode.New]: { name: 'Новый' },
    [ApsPlanStatusCode.Reopen]: { name: 'Переоткрыт' },
    [ApsPlanStatusCode.Work]: { name: 'В работе' },
    [ApsPlanStatusCode.Completed]: { name: 'Завершён' },
    [ApsPlanStatusCode.Deleted]: { name: 'Удалён' },
}

export enum PlanValidationStatusCode {
    Unknown = 'unknown',
    Valid = 'valid',
    Error = 'error',
    Warning = 'warning',
}

export type ApsPlanStatus = EntityStatus<ApsPlanStatusCode>;

export enum PlanType {
    OperationalPlan = 'OP',
    VolumeAndCalendarPlan = 'VCP',
}

export type TypeSpec = {
    name: string,
    code: PlanType,
}

export const PlanTypeSpec: { [key in PlanType]: Omit<TypeSpec, 'code'> } = {
    [PlanType.OperationalPlan]: { name: 'Оперативный план' },
    [PlanType.VolumeAndCalendarPlan]: { name: 'Объёмно-календарный план' },
}

export const PlanTypeList: TypeSpec[] = Object.keys(PlanTypeSpec).map(code => ({
    ...PlanTypeSpec[code as keyof typeof PlanTypeSpec],
    code: code as PlanType,
}));

export enum PlanCalculationAlgorithm {

    /** Дискретное производство */
    Discrete = 'discrete',

    /** Кампании */
    Campaign = 'campaign',

    /** По тактам */
    Tact = 'tact',

    /** FMCG производство */
    FMCG = 'fmcg',

    /** Критериальные отходы */
    CriterialWaste = 'criterial-waste',
};

export const PlanCalculationAlgorithmSpec: { [key in PlanCalculationAlgorithm]: Omit<TypeSpec, 'code'> } = {
    [PlanCalculationAlgorithm.Discrete]: { name: 'Дискретное производство' },
    [PlanCalculationAlgorithm.Campaign]: { name: 'Кампании' },
    [PlanCalculationAlgorithm.Tact]: { name: 'По тактам' },
    [PlanCalculationAlgorithm.FMCG]: { name: 'FMCG производство' },
    [PlanCalculationAlgorithm.CriterialWaste]: { name: 'Критериальные отходы' },
}

export const WorkWithPurchasedMaterialsTypes: { [key in MaterialPlanMode]: Omit<TypeSpec, 'code'> } = {
    [MaterialPlanMode.UseDelivery]: { name: 'Учёт плана поставки покупных материалов' },
    [MaterialPlanMode.CreateRequirement]: { name: 'Режим формирования потребности в покупных материалах' },
    [MaterialPlanMode.Disabled]: { name: 'Без учёта потребности и остатков покупных материалов' },
}

export type ShortValidationResult = {
    status: PlanValidationStatusCode,
    errors: number,
    warnings: number,
}

export type IApiPlan = {
    id: number,
    name: string,
    status: ApsPlanStatus,
    description: string,
    periodFrom: string,
    periodTo: string,
    createdAtUtc: string,
    createdBy: IApiUserAccount,
    changedAtUtc?: string,
    changedBy: IApiUserAccount,
    publishedVersionId?: number,
    publishedPlanScheduleId?: number,
    deletedAtUtc?: string,
    deletedBy: IApiUserAccount,
    lastVersionId?: number,
    varianceVersionId?: number,
    varianceVersionCreatedAtUtc?: string,
    lastCalculationId?: string,
    lastCalculationStartedAtUtc?: string,
    lastCalculationFinishedAtUtc?: string,
    lastVersionCreatedAtUtc?: string,
    createdAt: number,
    isFavorite: boolean,
    isPrivate: boolean,
    linkedWithMes: boolean,
    planType: PlanType,
    rawDataChangedAtUtc: UtcDateString,
    rawDataValidationStatusCode: PlanValidationStatusCode,
    rawDataValidatedAt?: UtcDateString,
    rawDataValidationProps?: ShortValidationResult,
    currentVersionId?: number,
}

export type IApiEditPlanParams = {
    name: string,
    description?: string,
    periodFrom: string | null,
    periodTo: string | null,
}

export type ElementValidationResult = {
    code: number,
    rule: string,
    message: string,
    solution: string,
}

export type RawDataValidationError = {
    table: string,
    errors?: ElementValidationResult[],
    warnings?: ElementValidationResult[],
}

export type PlanDataValidationResult = {
    progress: number,
    currentStep?: string,
    errCount: number,
    warnCount: number,
    findedErrors: RawDataValidationError[],
}

export type PlanWarning = {
    code: number,
    desc: string,
    calcTypes: string[],
}

export type GetPlanWarningsResult = {
    name: string,
    warnings: PlanWarning[],
}

export type SynchronizationChanges = {
    changed?: number,
    deleted?: number,
}

export type SynchronizationInfo = {
    importOrders: {
        orders: SynchronizationChanges,
    },
    importReferenceData: {
        technologies: SynchronizationChanges,
        workplaces: SynchronizationChanges,
        bacthSizes: SynchronizationChanges,
    },
    importFact: {
        wip: SynchronizationChanges,
        endProducts: SynchronizationChanges,
        readyAmount: SynchronizationChanges,
        hasFactDate: boolean,
    },
}

export type SynchronizationData = {
    sessionGUID: string,
    syncInfo: SynchronizationInfo,
}

export enum SynchronizationImportType {
    ImportReferenceData = 'importReferenceData',
    ImportOrders = 'importOrders',
    ImportFact = 'importFact',
}

export enum SynchronizationEntitiesType {
    Orders = 'orders',
    Wip = 'wip',
    EndProducts = 'endProducts',
    Technologies = 'technologies',
    Workplaces = 'workplaces',
    BacthSizes = 'bacthSizes',
    ReadyAmount = 'readyAmount',
}

export type SynchronizationTypeSpec = {
    importType: SynchronizationImportType,
    name: string,
    code: SynchronizationEntitiesType,
}

export const SynchronizationEntitiesTypeSpec: { [key in SynchronizationEntitiesType]: Omit<SynchronizationTypeSpec, 'code'> } = {
    [SynchronizationEntitiesType.Orders]: { name: 'Заказы', importType: SynchronizationImportType.ImportOrders },
    [SynchronizationEntitiesType.Wip]: { name: 'НЗП', importType: SynchronizationImportType.ImportFact },
    [SynchronizationEntitiesType.EndProducts]: { name: 'Готовая продукция', importType: SynchronizationImportType.ImportFact },
    [SynchronizationEntitiesType.Technologies]: { name: 'Технологии', importType: SynchronizationImportType.ImportReferenceData },
    [SynchronizationEntitiesType.Workplaces]: { name: 'Рабочие места', importType: SynchronizationImportType.ImportReferenceData },
    [SynchronizationEntitiesType.BacthSizes]: { name: 'Ограничение размера партии изготовления', importType: SynchronizationImportType.ImportReferenceData },
    [SynchronizationEntitiesType.ReadyAmount]: { name: 'Сделано по заказу', importType: SynchronizationImportType.ImportFact },
}

export const SynchronizationEntitiesTypeList: SynchronizationTypeSpec[] = Object.keys(SynchronizationEntitiesTypeSpec).map(code => ({
    ...SynchronizationEntitiesTypeSpec[code as keyof typeof SynchronizationEntitiesTypeSpec],
    code: code as SynchronizationEntitiesType,
}));

export type PlanSynchronizationParams = {
    sessionGUID: string,
    importData: string[],
}

export type PlanEntity = {
    id: string,
    name: string,
}

export enum ApiCalculationType {
    Schedule = 'schedule',
    Adaptive = 'adaptive',
    Iterative = 'iterative',
    Forecast = 'forecast',
    Optimization = 'optimization',
}

export type ApiPlanCalculationParams = {
    w: number[],
    sseChannelUid?: string,
}

export type ApiPlanValidationParams = {
    warningCodes?: number[],
    sseChannelUid?: string,
}

export type ApiParsedEntity = {
    entityType: string,
    name: string,
    count: number,
    hasError: boolean,
    hasWarning: boolean,
    hasPermission?: boolean,
}


export type ApiUploadForImportResult = {
    entities: ApiParsedEntity[],
    fileId: string,
    logPath?: string,
}

export type ImportEntry = {
    id: string,
    count: number,
}